import { Component, OnInit, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil, switchMap } from 'rxjs/operators';
import { CustomerService } from '../../services/customer.service';
import { PartnerCustomerSearchDto } from '../../models/customer.model';
import { sanitizeSearchTerm } from '../../utils/search.utils';

@Component({
    selector: 'app-customer-search',
    standalone: true,
    imports: [CommonModule, FormsModule],
    templateUrl: './customer-search.component.html',
    styleUrl: './customer-search.component.css'
})
export class CustomerSearchComponent implements OnInit, OnDestroy {
    @ViewChild('searchInput') searchInput!: ElementRef<HTMLInputElement>;

    searchTerm: string = '';
    customers: PartnerCustomerSearchDto[] = [];
    totalCount: number = 0;
    pageIndex: number = 0;
    pageSize: number = 10;
    isLoading: boolean = false;
    errorMessage: string = '';
    hasSearched: boolean = false;

    private searchSubject = new Subject<string>();
    private destroy$ = new Subject<void>();

    constructor(
        private customerService: CustomerService,
        private router: Router
    ) { }

    ngOnInit(): void {
        // Setup debounced search
        this.searchSubject
            .pipe(
                debounceTime(400),
                distinctUntilChanged(),
                takeUntil(this.destroy$),
                switchMap(term => {
                    const sanitized = sanitizeSearchTerm(term);
                    if (sanitized.length < 2) {
                        this.customers = [];
                        this.totalCount = 0;
                        this.hasSearched = false;
                        this.isLoading = false;
                        return [];
                    }

                    this.isLoading = true;
                    this.errorMessage = '';
                    this.hasSearched = true;
                    return this.customerService.searchCustomers(sanitized, this.pageIndex, this.pageSize);
                })
            )
            .subscribe({
                next: (response) => {
                    if (response) {
                        this.customers = response.items;
                        this.totalCount = response.totalCount;
                    }
                    this.isLoading = false;
                    // Keep focus on search input after results load
                    setTimeout(() => {
                        this.searchInput?.nativeElement?.focus();
                    }, 0);
                },
                error: (error) => {
                    console.error('Error searching customers:', error);
                    this.errorMessage = 'Failed to search customers. Please try again.';
                    this.isLoading = false;
                    this.customers = [];
                    this.totalCount = 0;
                }
            });
    }

    onSearchChange(value: string): void {
        this.searchTerm = value;
        this.pageIndex = 0; // Reset to first page on new search
        this.searchSubject.next(value);
    }

    onCustomerClick(customer: PartnerCustomerSearchDto): void {
        // Navigate to customer vouchers page
        this.router.navigate(['/customer', customer.customerId, 'vouchers'], {
            state: { customerName: customer.name }
        });
    }

    loadMore(): void {
        if (this.customers.length < this.totalCount && !this.isLoading) {
            this.pageIndex++;
            this.isLoading = true;
            this.errorMessage = '';

            this.customerService.searchCustomers(sanitizeSearchTerm(this.searchTerm), this.pageIndex, this.pageSize)
                .pipe(takeUntil(this.destroy$))
                .subscribe({
                    next: (response) => {
                        this.customers = [...this.customers, ...response.items];
                        this.totalCount = response.totalCount;
                        this.isLoading = false;
                    },
                    error: (error) => {
                        console.error('Error loading more customers:', error);
                        this.errorMessage = 'Failed to load more customers. Please try again.';
                        this.isLoading = false;
                        this.pageIndex--; // Revert page index on error
                    }
                });
        }
    }

    get hasMoreResults(): boolean {
        return this.customers.length < this.totalCount;
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
